import { css } from '@emotion/react';
import { FC } from 'react';
import {
  deriveDealDocDisplayStatus,
  IRoundDocumentMetadata,
  RoundStatus,
} from '../../../../../data-models/round-document.data-model';
import { colors } from '../../../../../theme/colors';

/**
 * Derive a round's doc-completeness status from its documents:
 * none → pending_docs; some but none extracted → docs_uploaded; any extracted → data_extracted.
 */
export function calculateRoundStatus(documents: IRoundDocumentMetadata[]): RoundStatus {
  if (!documents || documents.length === 0) {
    return 'pending_docs';
  }
  const statuses = documents.map(deriveDealDocDisplayStatus);
  // A failed doc needs attention even when others extracted fine.
  if (statuses.includes('Failed')) {
    return 'extraction_failed';
  }
  if (statuses.includes('Extracted')) {
    return 'data_extracted';
  }
  return 'docs_uploaded';
}

interface StatusConfig {
  label: string;
  color: string;
}

/**
 * Two doc states only — no counts, no "missing N" / "in progress" copy.
 * The card shows whether any documents have been uploaded for the round:
 * none → "No Docs Uploaded"; at least one uploaded → "Docs Uploaded".
 * (Per-document status lives in the Documents table, not on the card.)
 */
const statusConfigs: Record<RoundStatus, StatusConfig> = {
  pending_docs: { label: 'No Docs Uploaded', color: colors.warning[60] },
  docs_uploaded: { label: 'Docs Uploaded', color: colors.success[60] },
  data_extracted: { label: 'Docs Uploaded', color: colors.success[60] },
  extraction_failed: { label: 'Extraction Failed', color: colors.critical[50] },
};

const labelCss = (color: string) => css`
  font-size: 12px;
  line-height: 1.66;
  color: ${color};
  white-space: nowrap;
`;

interface DealDocStatusBadgeProps {
  status: RoundStatus;
  className?: string;
}

/**
 * Doc-completeness status for a financing round's deal documents.
 */
export const DealDocStatusBadge: FC<DealDocStatusBadgeProps> = ({ status, className }) => {
  const { label, color } = statusConfigs[status];
  return (
    <span css={labelCss(color)} className={className}>
      {label}
    </span>
  );
};
